import { useEffect, useMemo, type MouseEvent } from "react";
import { Icon } from "@blueprintjs/core";
import { ButtonWithDialog, StatusProxy } from "@focus/focusUI";
import { usePhonebook } from "../hooks/usePhonebook";
import { getFieldValue, getRecordEventId } from "../utils/recordValues";
import { Table } from "./table/Table";
import { TableShell } from "./table/TableShell";
import "./MyButton.scss";

export interface ContactsByOperatorSelection {
  operator: string;
  rowId?: string | number;
  eventId?: string | number;
  targetName?: string;
  chaserName?: string;
  timeOfCloseApproach?: string;
}

interface ContactsByOperatorButtonProps {
  data: any;
  onSelect?: (selection: ContactsByOperatorSelection) => void;
}

const getConjunctionOperator = (row: any) =>
  getFieldValue(row?.chaser?.spaceObject?.operator) ??
  getFieldValue(row?.chaser?.operator) ??
  getFieldValue(row?.operator) ??
  "";

const getContactsSelection = (
  data: any,
): ContactsByOperatorSelection | null => {
  const row = data?.row?.original;
  const operator = getConjunctionOperator(row);

  if (!operator) return null;

  return {
    operator,
    rowId: row?.id,
    eventId:
      getRecordEventId(row) ??
      getFieldValue(row?.conjunctionIdentifier) ??
      row?.id,
    targetName:
      getFieldValue(row?.targetName) ??
      getFieldValue(row?.target?.spaceObject?.name) ??
      getFieldValue(row?.target?.name),
    chaserName:
      getFieldValue(row?.chaserName) ??
      getFieldValue(row?.chaser?.spaceObject?.name) ??
      getFieldValue(row?.chaser?.name),
    timeOfCloseApproach: getFieldValue(row?.timeOfCloseApproach),
  };
};

const stopActionEvent = (event: MouseEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const ConjunctionPhonebookTable = ({ data }) => {
  const {
    contactsByOperator,
    getOrganizationMembers,
    getRequestStatus,
    getRequestError,
  } = usePhonebook();
  const columnDefs = [
    {
      headerName: "Name",
      field: "contactName",
      key: "contactName",
      hide: false,
    },
    {
      headerName: "Role",
      field: "role",
      key: "role",
    },

    {
      headerName: "Phone",
      field: "phoneNumber",
      key: "phoneNumber",
    },
    {
      headerName: "Email",
      field: "email",
      key: "email",
    },
  ];

  const serverDisplayConfig = columnDefs.reduce(
    (acc, c, i) => ({ ...acc, [c.key]: { visible: true, order: i } }),
    {},
  );

  const operator = getContactsSelection(data)?.operator;

  useEffect(() => {
    if (operator) {
      getOrganizationMembers(operator);
    }
  }, [operator]);

  const items = useMemo(
    () => (operator ? contactsByOperator[operator] : []),
    [operator, contactsByOperator],
  );

  const status = getRequestStatus("getContactsByOperatorName");
  const error = getRequestError("getContactsByOperatorName");

  return (
    <div className="h-[420px] w-full p-6">
      <StatusProxy status={status} error={error}>
        <TableShell className="contacts-by-operator-table-shell">
          <Table
            columnDefinitions={columnDefs}
            serverDisplayConfig={serverDisplayConfig}
            defaultVisibleValues={columnDefs.map((c) => c.key)}
            rowData={items}
            base={"phonebook"}
            height="auto"
            actions={undefined}
            showOperatorColumn={false}
            showSidebar={false}
            showGlobalFilter={false}
            paginated={false}
          />
        </TableShell>
      </StatusProxy>
    </div>
  );
};

export const ContactsByOperatorButton: React.FC<
  ContactsByOperatorButtonProps
> = ({ data, onSelect }) => {
  const selection = getContactsSelection(data);
  const disabled = !selection?.operator;

  if (onSelect) {
    return (
      <button
        aria-label={
          selection?.operator
            ? `Show ${selection.operator} contacts`
            : "No operator contacts available"
        }
        className={`contacts-operator-button focusoc-table-action-icon ${
          disabled ? "focusoc-table-action-icon-disabled" : ""
        }`}
        disabled={disabled}
        onDoubleClick={stopActionEvent}
        onClick={(event) => {
          stopActionEvent(event);

          if (selection) {
            onSelect(selection);
          }
        }}
        onMouseDown={stopActionEvent}
        title={
          selection?.operator
            ? `Show ${selection.operator} contacts`
            : "No operator contacts available"
        }
        type="button"
      >
        <Icon icon="book" title={false} />
      </button>
    );
  }

  return (
    <ButtonWithDialog
      className="contacts-operator-button focusoc-table-action-button"
      headerTitle={`Operator ${selection?.operator ?? ""} Contacts`}
      minimal
      intent="none"
      icon="book"
      disabled={disabled}
      form={() => <ConjunctionPhonebookTable data={data} />}
      hideButtons
      onAccept={function (content: unknown): void {
        throw new Error("Function not implemented.");
      }}
    />
  );
};
